import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const solutions = [
  {
    title: "Smart Lighting Control",
    description: "Automated lighting systems allow homeowners to control brightness, scheduling, and energy usage across different areas of the house. With intelligent lighting management, residents can create customized environments that match their lifestyle while improving energy efficiency.",
    items: [
      "Remote lighting control via mobile devices",
      "Automated lighting schedules",
      "Motion-based lighting activation",
      "Energy-efficient lighting management"
    ],
    icon: "Lightbulb",
    type: "Features"
  },
  {
    title: "Intelligent Climate Management",
    description: "Smart climate control systems help maintain comfortable indoor conditions while optimizing energy consumption. Integrated temperature management allows users to monitor and adjust cooling systems automatically or remotely.",
    items: [
      "Remote temperature adjustment",
      "Automated climate scheduling",
      "Energy-efficient cooling management",
      "Integration with HVAC systems"
    ],
    icon: "Thermometer",
    type: "Benefits"
  },
  {
    title: "Smart Security Systems",
    description: "Advanced home security technologies provide continuous monitoring and protection. These systems offer homeowners complete visibility and control over security devices, ensuring peace of mind whether they are at home or away.",
    items: [
      "Smart surveillance cameras",
      "Motion detection sensors",
      "Remote door access control",
      "Real-time security alerts"
    ],
    icon: "Shield",
    type: "Solutions"
  },
  {
    title: "Smart Entertainment Systems",
    description: "Integrated entertainment systems allow users to control audio and video experiences throughout the home. Multi-room audio and smart media management create a connected entertainment environment.",
    items: [
      "Centralized audio control",
      "Multi-room sound systems",
      "Smart TV integration",
      "Voice-controlled entertainment"
    ],
    icon: "Tv",
    type: "Capabilities"
  }
];

const AutomationSolutions = () => {
  return (
    <section className="py-24 bg-slate-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-sm font-bold uppercase tracking-[0.4em] text-[#91756A] mb-4"
          >
            Integrated Home Automation Solutions
          </motion.h2>
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-4xl md:text-5xl font-bold text-[#4D4C4A] italic tracking-tighter"
          >
            A Seamless Digital Ecosystem.
          </motion.h3>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {solutions.map((solution, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              className="bg-white p-10 shadow-sm border border-slate-100 hover:shadow-xl transition-all duration-500 group"
            >
              <div className="flex items-start gap-6 mb-8">
                <div className="w-14 h-14 bg-[#4D4C4A] flex items-center justify-center shrink-0 group-hover:bg-[#91756A] transition-colors duration-300">
                  <Icon name={solution.icon} size={28} className="text-white" />
                </div>
                <div>
                  <h4 className="text-2xl font-bold text-[#4D4C4A] italic tracking-tight uppercase mb-4">
                    {solution.title}
                  </h4>
                  <p className="text-slate-500 leading-relaxed mb-6">
                    {solution.description}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#91756A] mb-4">{solution.type} Include:</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {solution.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 bg-[#91756A] rounded-full"></div>
                      <span className="text-sm text-slate-600 font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AutomationSolutions;